import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NotifyService } from 'app/core/services/notify/notify.service';
import { AuthenticatedLaboratoryService } from 'app/core/services/prothesis-api/authenticated/authenticated-laboratory.service';
import { AutoCompleteResultProjection } from 'app/shared/prothesis-api-projections/auto-complete-result.projection';
import { InfoLaboratoryViewModel } from 'app/shared/prothesis-api-view-models/info-laboratory.view-model';
import { PickerAllowedLaboratoryViewModel } from 'app/shared/prothesis-api-view-models/picker-allowed-laboratory.view-model';
import { TenantFilter } from 'app/shared/prothesis-base-api-filters/tenant-filter';
import { PickerState } from 'app/shared/states/picker-state';
import { Observable } from 'rxjs';
import { ElementModel } from '../picker/element.model';
import { PickerComponent } from '../picker/picker.component';
import { LaboratoryTenantModelHelper } from './model-helpers/laboratory-tenant-model-helper';
import { LaboratoryTenantModel } from './models/laboratory-tenant-model';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-laboratory-tenant-picker',
  templateUrl: './laboratory-tenant-picker.component.html',
  styleUrls: ['./laboratory-tenant-picker.component.scss'],
  imports: [PickerComponent],
})
export class LaboratoryTenantPickerComponent implements OnChanges {
  @ViewChild('_pickerComponent', { static: true }) public _pickerComponent: PickerComponent;
  @Output() public laboratoryTenantAddedEvent = new EventEmitter<LaboratoryTenantModel>();
  @Output() public pickerDialogAfterClosedEvent = new EventEmitter<void>();
  @Input() public textFilterDefaultValue: string;
  public autoCompleteResultList: AutoCompleteResultProjection[];
  public elements: ElementModel[] = new Array<ElementModel>();
  public existenceFlag = false;
  public loadedFlag: boolean;
  public pickerState: PickerState = new PickerState();
  public showPickerFlag = true;
  public totalCount = 0;
  private _authenticatedLaboratoryService = inject(AuthenticatedLaboratoryService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _notifyService = inject(NotifyService);
  private _viewModels: InfoLaboratoryViewModel[] = new Array<InfoLaboratoryViewModel>();

  public ngOnChanges(): void {
    if (this.textFilterDefaultValue) {
      this.textFilterDefaultValue = this.textFilterDefaultValue.trim();
    }
  }

  public pickerDialogAfterClosedEventHandler(): void {
    this.pickerDialogAfterClosedEvent.emit();
  }

  public selectElement(element: ElementModel): void {
    const viewModel = this._viewModels.find((c) => c.uuid === element.key);
    if (viewModel) {
      const models = LaboratoryTenantModelHelper.fromInfoLaboratoryViewModel([viewModel]);
      this.showPickerFlag = false;
      this.laboratoryTenantAddedEvent.emit(models[0]);
    }
  }

  public setFocus(): void {
    this._pickerComponent?.setFocus();
  }

  public updateState(pickerState: PickerState): void {
    this.pickerState = pickerState;
    this._refreshList();
  }

  public uuidSelectedHandler(uuid: string): void {
    const filter = new TenantFilter();
    filter.uuids = [uuid];
    this._authenticatedLaboratoryService.getAll(filter, 'code_asc', 0, 1).subscribe((indexManufacturerViewModel) => {
      if (indexManufacturerViewModel.resultList && indexManufacturerViewModel.resultList.values) {
        if (indexManufacturerViewModel.resultList.values.length === 1) {
          const models = LaboratoryTenantModelHelper.fromReadAllowedLaboratoryViewModel(indexManufacturerViewModel.resultList.values);
          this.laboratoryTenantAddedEvent.emit(models[0]);
        } else {
          this._notifyService.displayErrorMessagesNow(`Laboratoire inexistant.`);
        }
      }
    });
  }

  private _load(): Observable<PickerAllowedLaboratoryViewModel> {
    const filter = this._setPickerFilter();
    const sort = 'label_asc';
    return this._authenticatedLaboratoryService.initializePicker(filter, sort, this.pickerState.pageIndex, this.pickerState.pageSize);
  }

  private _refreshList(): void {
    this.loadedFlag = false;
    this._load().subscribe((result: PickerAllowedLaboratoryViewModel) => {
      this.existenceFlag = result.existenceFlag;

      // Handle resultList
      if (result.resultList) {
        this.totalCount = result.resultList.aggregate?.count;

        // View model to model conversion
        this.elements = [];
        this._changeDetectorRef.markForCheck();
        this._viewModels = result.resultList.values;
        const models = LaboratoryTenantModelHelper.fromInfoLaboratoryViewModel(this._viewModels);
        models.forEach((model) => {
          this.elements.push(new ElementModel(model.code, 'Laboratoire', model.uuid, model.name, null));
        });
      }

      this.loadedFlag = true;
      this._notifyService.displayMessages();
    });
  }

  private _setPickerFilter(): TenantFilter {
    const filter = new TenantFilter();
    filter.textFilter = this.pickerState.textFilter;
    filter.textFilterFields = ['name'];
    return filter;
  }
}
